import { TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

export type TableParamsProps = Required<
  Pick<TablePaginationConfig, 'current' | 'pageSize'>
> &
  Pick<TablePaginationConfig, 'position'> & {
    sortField: SorterResult<unknown>['field'];
    sortOrder: SorterResult<unknown>['order'];
  };

export const getDefaultTableParams = ({
  defaultCurrentPage = DEFAULT_CURRENT_PAGE,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  defaultSortField = 'id',
  defaultSortOrder = 'descend',
}: {
  defaultCurrentPage?: number;
  defaultPageSize?: number;
  defaultSortField?: string;
  defaultSortOrder?: 'ascend' | 'descend';
} = {}): TableParamsProps => {
  return {
    current: defaultCurrentPage,
    pageSize: defaultPageSize,
    sortField: defaultSortField,
    sortOrder: defaultSortOrder,
  } satisfies TableParamsProps;
};

const TableParamsContext = createContext<{
  tableParams: TableParamsProps;
  handleChange: (newTableParams: TableParamsProps) => void;
  defaultTableParams: TableParamsProps;
}>({
  tableParams: getDefaultTableParams(),
  handleChange: () => {},
  defaultTableParams: getDefaultTableParams(),
});

const TableParamsProvider = ({
  children,
  defaultSortField = 'id',
  defaultSortOrder = 'descend',
  defaultPageSize,
  defaultCurrentPage,
}: PropsWithChildren<{
  defaultSortField?: string;
  defaultSortOrder?: 'ascend' | 'descend';
  defaultPageSize?: number;
  defaultCurrentPage?: number;
}>) => {
  const defaultTableParams = useMemo(
    () =>
      getDefaultTableParams({
        defaultSortField,
        defaultSortOrder,
        defaultPageSize,
        defaultCurrentPage,
      }),
    [defaultSortField, defaultSortOrder, defaultPageSize, defaultCurrentPage],
  );

  const [tableParams, setTableParams] =
    useState<TableParamsProps>(defaultTableParams);

  const handleChange = (newTableParams: TableParamsProps) => {
    setTableParams(() => ({ ...defaultTableParams, ...newTableParams }));
  };

  return (
    <TableParamsContext.Provider
      value={{ tableParams, handleChange, defaultTableParams }}
    >
      {children}
    </TableParamsContext.Provider>
  );
};

const useTableParams = () => {
  const { tableParams, handleChange, defaultTableParams } =
    useContext(TableParamsContext);
  return { tableParams, setTableParams: handleChange, defaultTableParams };
};

export { TableParamsContext, TableParamsProvider, useTableParams };
